import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { default as ARCStatusBarMenu } from "components/ARC/StatusBarMenu";
import FunctionalButton from "components/layout/FunctionalButton";
import FullscreenButton from "components/layout/FullscreenButton";
import NotificationButton from "components/layout/notification/NotificationButton";
import { default as RobotStatusBar } from "components/robot/StatusBar";
import EstopToggleButton from "components/control/EstopToggleButton";
import { useMissionControl } from "providers/navigation/MissionProvider";
import { useOrgFleet } from "providers/OrgFleetProvider";
import { useDeviceFeatureFlags } from "providers/DeviceFeatureFlagsProvider";
import { arcLogout } from "lib/arcAuthApi";

import { ReactComponent as LogPageIcon } from "assets/svgs/log_page.svg";
import { ReactComponent as SettingIcon } from "assets/svgs/navbar/Setting.svg";
import { ReactComponent as LogoutIcon } from "assets/svgs/navbar/Logout.svg";
import { ReactComponent as DetailsIcon } from "assets/svgs/Details.svg";
import { ReactComponent as GridIcon } from "assets/svgs/navbar/Grid.svg";

const TopBar = ({ arc, arcStatus }) => {
    const { t } = useTranslation("control");
    const location = useLocation();
    const navigate = useNavigate();
    // Normalize trailing slashes: endsWith("/settings") misses `/.../settings/`, and then
    // relative `navigate("settings")` resolves to `.../settings/settings` (invalid route).
    const path = location.pathname.replace(/\/+$/, "") || "/";
    const onDeviceSettings = path.endsWith("/settings");
    const onLogsPage = path.endsWith("/logs");

    const robot = arc?.robot;
    const robotStatus = arcStatus?.robot;
    const { flags, hostname, temperature, cpuPercent } = useDeviceFeatureFlags();
    const { HAS_MISSIONS } = flags;
    const { showBehaviourInControl, setShowBehaviourInControl } = useMissionControl();

    // Fleet mode is truthy only when OrgFleetProvider is mounted (fleet/arc/:arcId route);
    // null in single-ARC mode. FleetProvider is deprecated — use OrgFleetProvider.
    const orgFleet = useOrgFleet();
    const isFleetMode = !!orgFleet;
    // In fleet mode show the fleet_server display name, with the serial underneath.
    const { arcId } = useParams();
    const fleetArc = orgFleet?.arcsInfo?.find((info) => info.serialNumber === arcId);

    return (
        <div className="flex h-topBar w-full min-w-0 items-center justify-between gap-x-2 overflow-x-auto overflow-y-hidden bg-chrome text-white py-[1.375rem] pl-20 pr-3 md:pr-5">
            <div className="flex min-w-0 flex-1 items-center pr-1 md:pr-2">
                <RobotStatusBar
                    name={fleetArc?.name || hostname || robot?.name || ""}
                    serialNumber={fleetArc?.serialNumber}
                    temperature={temperature ?? robotStatus?.temperature}
                    cpuPercent={cpuPercent ?? robotStatus?.cpu_percent}
                />
            </div>
            <div className="flex shrink-0 items-center gap-x-1.5 md:gap-x-2">
                {isFleetMode ? (
                    <>
                        <FunctionalButton
                            icon={<GridIcon className="h-6 w-6" />}
                            className="h-9 w-9 text-white"
                            hoverText={t("topBar.fleet", "Fleet dashboard")}
                            onClick={() => navigate("/fleet/dashboard")}
                        />
                        <div className="mx-1 h-6 w-px shrink-0 bg-white/20" aria-hidden="true" />
                    </>
                ) : (
                    <>
                        <FunctionalButton
                            icon={<LogoutIcon className="h-6 w-6" />}
                            className="h-9 w-9 text-white"
                            hoverText={t("topBar.logout", "Logout")}
                            onClick={() => {
                                void arcLogout().then(() => navigate("/arc/login", { replace: true }));
                            }}
                        />
                        <div className="mx-1 h-6 w-px shrink-0 bg-white/20" aria-hidden="true" />
                    </>
                )}
                {HAS_MISSIONS && (
                    <FunctionalButton
                        icon={<DetailsIcon className="h-6 w-6" />}
                        className={`h-8 w-8 text-white ${showBehaviourInControl ? "rounded-r3 bg-primary2 text-on-accent" : ""}`}
                        hoverText={t("topBar.behaviour")}
                        onClick={() => setShowBehaviourInControl(!showBehaviourInControl)}
                    />
                )}
                <NotificationButton
                    className="h-8 w-8 text-white"
                    iconClassName="h-6 w-6"
                />
                <FullscreenButton className="h-8 w-8 text-white" iconClassName="h-6 w-6" />
                {HAS_MISSIONS &&
                    (onLogsPage ? (
                        <div className="h-9 w-9 shrink-0 pointer-events-none" aria-hidden />
                    ) : (
                        <FunctionalButton
                            icon={<LogPageIcon className="h-6 w-6" />}
                            className="h-9 w-9 text-white"
                            hoverText={t("topBar.logs")}
                            onClick={() => navigate("logs")}
                        />
                    ))}
                {onDeviceSettings ? (
                    <div className="h-9 w-9 shrink-0 pointer-events-none" aria-hidden />
                ) : (
                    <FunctionalButton
                        icon={<SettingIcon className="h-6 w-6" />}
                        className="h-9 w-9 text-white"
                        onClick={() => navigate("settings")}
                    />
                )}
                <ARCStatusBarMenu />
                <EstopToggleButton />
            </div>
        </div>
    );
};

export default TopBar;
